import { Badge, Button, Group, Loader, Stack, Text, Tooltip } from '@mantine/core';
import {
  IconArrowRight,
  IconCircleCheck,
  IconHelpCircle,
  IconInbox,
  IconInfoCircle,
  IconSend,
  IconSparkles,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import type { ZendeskDraftSummary, ZendeskMetricMeta, ZendeskStats } from '@mygpt/app/apis/services/zendesk';
import type { QueueFilter } from './Queue';
import {
  ACCENT,
  CARD_SHADOW,
  EDIT_REASONS,
  REVIEW_OUTCOMES,
  formatDateTime,
  formatPercent,
  formatSimilarity,
  reasonLabel,
  statusColor,
  statusLabel,
} from './shared';

// 効果タブ：1 画面に収める（KPI 3 つ → 推移 1 枚 → 届かなかった理由 1 枚 → 対応 2 枚）。
// 類似度分布・日次件数・改善結果の棒は落とした（発送構成 / 推移 / 一覧と重複していた）
const RECENT_LIMIT = 5;

// 指標の口径（単位 / 分子 / 分母 / 期間 / 除外）をラベルのツールチップで示す
const MetaTip = ({ meta, children }: { meta?: ZendeskMetricMeta; children: React.ReactNode }) => {
  const { t } = useTranslation();
  if (!meta) return <>{children}</>;
  const opts = { ...meta, interpolation: { escapeValue: false } };
  return (
    <Tooltip
      withArrow
      multiline
      w={340}
      label={
        <Stack gap={2}>
          <Text fz={11}>{t('zendesk_ops.kpi.meta_line', opts)}</Text>
          {!!meta.excludes?.length && (
            <Text fz={11}>
              {t('zendesk_ops.kpi.meta_excludes', {
                list: meta.excludes.join(' / '),
                interpolation: { escapeValue: false },
              })}
            </Text>
          )}
        </Stack>
      }
    >
      <span style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
        {children}
        <IconInfoCircle size={12} color="#ADB5BD" style={{ flexShrink: 0 }} />
      </span>
    </Tooltip>
  );
};

const KPI_COLORS = {
  as_is: { color: '#0CA678', soft: 'rgba(12,166,120,0.10)' },
  generation: { color: ACCENT, soft: 'rgba(3,54,61,0.10)' },
  unanswerable: { color: '#FD7E14', soft: 'rgba(253,126,20,0.10)' },
};
const BREAKDOWN_COLORS = { as_is: '#0CA678', edited: '#FD7E14', self: '#FA5252' };
// 知識不足だけ橙（対応が要る）、あとは落ち着いた色
const REASON_COLORS: Record<string, string> = {
  knowledge_gap: '#FD7E14',
  case_specific: '#4C6EF5',
  tone: '#20C997',
  policy: '#7C68FB',
  out_of_scope: '#868E96',
  unclassified: '#CED4DA',
};
const SKIP_COLORS: Record<string, string> = {
  knowledge_gap: '#FD7E14',
  unanswerable: '#FFA94D',
  generation_failed: '#FA5252',
  non_question: '#ADB5BD',
  form_excluded: '#CED4DA',
};
const BAR_SOFT = 'rgba(3,54,61,0.14)';

const Card = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div className="rounded-xl bg-white" style={{ boxShadow: CARD_SHADOW, padding: 16, ...style }}>
    {children}
  </div>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Text fz={13} fw={700} c="gray.7" mb={8}>
    {children}
  </Text>
);

// 前期比：rate 差 × 100 = pt；lowerIsBetter 的指标（未回答率）下降为好
const Delta = ({ now, prev, lowerIsBetter }: { now: number | null; prev: number | null; lowerIsBetter?: boolean }) => {
  const { t } = useTranslation();
  if (now === null || prev === null) {
    return (
      <Text fz={11} c="gray.5">
        {t('zendesk_ops.kpi.no_prev')}
      </Text>
    );
  }
  const pt = Math.round((now - prev) * 100);
  const good = lowerIsBetter ? pt < 0 : pt > 0;
  const color = pt === 0 ? 'gray.5' : good ? 'teal.7' : 'red.6';
  const arrow = pt === 0 ? '±' : pt > 0 ? '▲' : '▼';
  return (
    <Text fz={11} c={color} fw={600}>
      {arrow} {pt > 0 ? '+' : ''}
      {pt}pt {t('zendesk_ops.kpi.vs_prev')}
    </Text>
  );
};

// 日別：判定済み件数（柱）＋ そのまま送信率（線）。同じ分母なので 1 枚で読める
const ComboChart = ({ daily }: { daily: ZendeskStats['daily'] }) => {
  const { t } = useTranslation();
  const n = daily.length;
  const maxAnswered = Math.max(0, ...daily.map((d) => d.answered || 0));
  const withRate = daily.map((d, i) => ({ ...d, i })).filter((d) => d.as_is_rate !== null);
  if (!n || (maxAnswered === 0 && withRate.length === 0)) {
    return (
      <Text fz={12} c="gray.5" py={24} ta="center">
        {t('zendesk_ops.trend.empty')}
      </Text>
    );
  }
  const W = 900;
  const H = 140;
  const padL = 36;
  const padR = 12;
  const padT = 12;
  const padB = 22;
  const slot = (W - padL - padR) / n;
  const bw = Math.max(2, Math.min(16, slot * 0.6));
  const xc = (i: number) => padL + slot * i + slot / 2;
  const ys = (r: number) => padT + (H - padT - padB) * (1 - r);
  const bh = (v: number) => (maxAnswered ? ((H - padT - padB) * v) / maxAnswered : 0);
  const labelEvery = Math.max(1, Math.ceil(n / 8));
  const shortDay = (day: string) => day.slice(5).replace('-', '/');
  const poly = withRate.map((p) => `${xc(p.i).toFixed(1)},${ys(p.as_is_rate as number).toFixed(1)}`).join(' ');
  const last = withRate[withRate.length - 1];
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={t('zendesk_ops.trend.combo_title')}>
        {[0, 0.5, 1].map((g) => (
          <g key={g}>
            <line x1={padL} x2={W - padR} y1={ys(g)} y2={ys(g)} stroke="#EEF0F4" strokeDasharray="3 3" />
            <text x={padL - 8} y={ys(g) + 4} fontSize={10} fill="#9AA0AC" textAnchor="end">
              {Math.round(g * 100)}%
            </text>
          </g>
        ))}
        {daily.map((d, i) => {
          const h = bh(d.answered || 0);
          return (
            <g key={d.day}>
              <title>
                {d.day}: {t('zendesk_ops.trend.answered')} {d.answered || 0} / {t('zendesk_ops.breakdown.as_is')}{' '}
                {d.as_is || 0} ({formatPercent(d.as_is_rate)})
              </title>
              {h > 0 && <rect x={xc(i) - bw / 2} y={H - padB - h} width={bw} height={h} fill={BAR_SOFT} rx={2} />}
              {(i % labelEvery === 0 || i === n - 1) && (
                <text x={xc(i)} y={H - 6} fontSize={10} fill="#9AA0AC" textAnchor="middle">
                  {shortDay(d.day)}
                </text>
              )}
            </g>
          );
        })}
        {withRate.length > 1 && (
          <polyline
            points={poly}
            fill="none"
            stroke={ACCENT}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {withRate.map((p) => (
          <circle
            key={p.day}
            cx={xc(p.i)}
            cy={ys(p.as_is_rate as number)}
            r={withRate.length > 40 ? 2.5 : 3.5}
            fill="#fff"
            stroke={ACCENT}
            strokeWidth={2}
          />
        ))}
        {last && (
          <text
            x={Math.min(xc(last.i), W - padR - 14)}
            y={ys(last.as_is_rate as number) - 10}
            fontSize={11}
            fontWeight={700}
            fill={ACCENT}
            textAnchor="middle"
          >
            {formatPercent(last.as_is_rate)}
          </text>
        )}
      </svg>
      <Group gap={14} mt={2}>
        <Group gap={6}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: BAR_SOFT, display: 'inline-block' }} />
          <Text fz={11.5} c="gray.6">
            {t('zendesk_ops.trend.answered')}
          </Text>
        </Group>
        <Group gap={6}>
          <span style={{ width: 14, height: 2, background: ACCENT, display: 'inline-block' }} />
          <Text fz={11.5} c="gray.6">
            {t('zendesk_ops.kpi.as_is_rate')}
          </Text>
        </Group>
      </Group>
    </>
  );
};

// 積み上げ 1 本 ＋ 件数のチップ（0 件は出さない）。発送構成 / 修正理由 / 未生成理由を同じ形で見せる
interface StripPart {
  key: string;
  label: string;
  count: number;
  color: string;
}

const Strip = ({ parts, total, empty }: { parts: StripPart[]; total: number; empty: string }) => {
  const shown = parts.filter((p) => p.count > 0);
  return (
    <>
      <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: '#F1F3F5' }}>
        {shown.map((p) => (
          <div
            key={p.key}
            style={{ width: total ? `${(p.count / total) * 100}%` : 0, background: p.color }}
            title={`${p.label} ${p.count}`}
          />
        ))}
      </div>
      <Group gap={14} mt={6}>
        {shown.length === 0 ? (
          <Text fz={11.5} c="gray.5">
            {empty}
          </Text>
        ) : (
          shown.map((p) => (
            <Group key={p.key} gap={6} wrap="nowrap">
              <span style={{ width: 10, height: 10, borderRadius: 2, background: p.color, display: 'inline-block' }} />
              <Text fz={11.5} c="gray.6" style={{ whiteSpace: 'nowrap' }}>
                {p.label} <b style={{ fontVariantNumeric: 'tabular-nums' }}>{p.count}</b>
              </Text>
            </Group>
          ))
        )}
      </Group>
    </>
  );
};

// 発送判定の構成（類似度分布はこの粗い版なので落とした。平均だけ添える）
const BreakdownStrip = ({ stats }: { stats: ZendeskStats }) => {
  const { t } = useTranslation();
  const answered = stats.answered || 0;
  const parts = (['as_is', 'edited', 'self'] as const).map((k) => ({
    key: k,
    label: t(`zendesk_ops.breakdown.${k}`),
    count: stats[k] || 0,
    color: BREAKDOWN_COLORS[k],
  }));
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #F2F3F7' }}>
      <Group justify="space-between" mb={6}>
        <Text fz={11.5} fw={600} c="gray.6">
          {t('zendesk_ops.breakdown.title')} · {t('zendesk_ops.breakdown.total_answered', { count: answered })}
        </Text>
        {stats.similarity_avg !== null && stats.similarity_avg !== undefined && (
          <Text fz={11.5} c="gray.5">
            {t('zendesk_ops.breakdown.similarity_avg', { value: stats.similarity_avg.toFixed(2) })}
          </Text>
        )}
      </Group>
      <Strip parts={parts} total={answered} empty={t('zendesk_ops.trend.empty')} />
    </div>
  );
};

const RecentList = ({
  items,
  onOpen,
  onViewAll,
}: {
  items: ZendeskDraftSummary[];
  onOpen: (d: ZendeskDraftSummary) => void;
  onViewAll: () => void;
}) => {
  const { t } = useTranslation();
  const shown = items.slice(0, RECENT_LIMIT);
  return (
    <Card style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Group justify="space-between" px={16} pt={14} pb={6}>
        <SectionTitle>{t('zendesk_ops.recent.title')}</SectionTitle>
        <Button
          size="compact-xs"
          variant="subtle"
          color="gray"
          rightSection={<IconArrowRight size={12} />}
          onClick={onViewAll}
          mb={8}
        >
          {t('zendesk_ops.recent.view_all')}
        </Button>
      </Group>
      {shown.length === 0 ? (
        <Stack align="center" py={24} gap={6}>
          <IconInbox size={24} color="#C0C4CC" />
          <Text fz={12} c="gray.5">
            {t('zendesk_ops.recent.empty')}
          </Text>
        </Stack>
      ) : (
        shown.map((d, i) => {
          const reason = d.edit_reason || d.edit_reason_ai;
          const isAi = !d.edit_reason && !!d.edit_reason_ai;
          return (
            <div
              key={d.id}
              onClick={() => onOpen(d)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 16px',
                borderTop: i === 0 ? '1px solid #F2F3F7' : 'none',
                borderBottom: '1px solid #F2F3F7',
                cursor: 'pointer',
                fontSize: 12.5,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#FAFAFE';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '';
              }}
            >
              <Text fw={700} fz={12.5} style={{ fontFamily: 'monospace', flex: '0 0 56px' }}>
                #{d.ticket_id}
              </Text>
              <Text fz={12.5} c="gray.8" truncate style={{ flex: '1 1 auto', minWidth: 0 }}>
                {d.ticket_subject || '—'}
              </Text>
              <Badge
                variant="light"
                color={statusColor(d.status)}
                size="xs"
                radius="sm"
                style={{ textTransform: 'none' }}
              >
                {statusLabel(t, d.status, d.skip_reason)}
              </Badge>
              <Text
                fz={11.5}
                c="gray.6"
                style={{ flex: '0 0 40px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
              >
                {formatSimilarity(d.similarity)}
              </Text>
              <Group gap={4} wrap="nowrap" style={{ flex: '0 0 110px', minWidth: 0 }}>
                <Text fz={11.5} c={reason ? 'gray.7' : 'gray.4'} truncate>
                  {reason ? reasonLabel(t, reason) : '—'}
                </Text>
                {isAi && <IconSparkles size={11} color="#7C68FB" style={{ flexShrink: 0 }} />}
              </Group>
              <Text fz={11.5} c="gray.5" style={{ flex: '0 0 84px', textAlign: 'right' }}>
                {formatDateTime(d.answered_at || d.updated_at)}
              </Text>
            </div>
          );
        })
      )}
    </Card>
  );
};

interface DashboardProps {
  stats: ZendeskStats | null;
  loading: boolean;
  recent: ZendeskDraftSummary[];
  onOpen: (d: ZendeskDraftSummary) => void;
  onGoQueue: (filter: QueueFilter) => void;
}

export const Dashboard = ({ stats, loading, recent, onOpen, onGoQueue }: DashboardProps) => {
  const { t } = useTranslation();
  if (loading && !stats) {
    return (
      <Group justify="center" py={60}>
        <Loader size="sm" />
      </Group>
    );
  }
  if (!stats) return null;

  const kpis = [
    {
      key: 'as_is',
      label: t('zendesk_ops.kpi.as_is_rate'),
      meta: stats.meta?.as_is_rate,
      value: formatPercent(stats.as_is_rate),
      Icon: IconSend,
      delta: <Delta now={stats.as_is_rate} prev={stats.previous.as_is_rate} />,
      ...KPI_COLORS.as_is,
    },
    {
      key: 'generation',
      label: t('zendesk_ops.kpi.generation_rate'),
      meta: stats.meta?.generation_rate,
      value: formatPercent(stats.generation_rate),
      Icon: IconCircleCheck,
      delta: <Delta now={stats.generation_rate} prev={stats.previous.generation_rate} />,
      ...KPI_COLORS.generation,
    },
    {
      key: 'unanswerable',
      label: t('zendesk_ops.kpi.unanswerable_rate'),
      meta: stats.meta?.unanswerable_rate,
      value: formatPercent(stats.unanswerable_rate),
      Icon: IconHelpCircle,
      delta: <Delta now={stats.unanswerable_rate} prev={stats.previous.unanswerable_rate} lowerIsBetter />,
      ...KPI_COLORS.unanswerable,
    },
  ];

  const reasonTotal = Object.values(stats.edit_reasons || {}).reduce((a, b) => a + b, 0);
  const skipTotal = Object.values(stats.by_skip_reason || {}).reduce((a, b) => a + b, 0);
  const improvement = stats.improvement;
  const reviewCounts = improvement || stats.review;
  const outcomeLine = REVIEW_OUTCOMES.filter((o) => (improvement?.outcomes?.[o] || 0) > 0)
    .map((o) => `${t(`zendesk_ops.outcome.${o}`)} ${improvement?.outcomes?.[o]}`)
    .join(' ・ ');
  const gapOpen = improvement?.knowledge_gap_open || 0;
  // 旧データの「回答不可」は件数があるときだけ行を出す
  const skipRows = (
    ['knowledge_gap', 'unanswerable', 'generation_failed', 'non_question', 'form_excluded'] as const
  ).filter((r) => r !== 'unanswerable' || (stats.by_skip_reason?.unanswerable || 0) > 0);

  return (
    <Stack gap={12}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        {kpis.map(({ key, label, meta, value, Icon, delta, color, soft }) => (
          <Card key={key} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '12px 16px' }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: soft,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon size={20} color={color} />
            </div>
            <div style={{ minWidth: 0 }}>
              <MetaTip meta={meta}>
                <Text fz={11.5} c="gray.6" truncate>
                  {label}
                </Text>
              </MetaTip>
              <Group gap={10} align="baseline" wrap="nowrap">
                <Text fz={24} fw={700} lh={1.15} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {value}
                </Text>
                {delta}
              </Group>
            </div>
          </Card>
        ))}
      </div>
      {stats.tickets && (
        <Text fz={11.5} c="gray.6" mt={-4}>
          {t('zendesk_ops.tickets.line', {
            tickets: stats.tickets.tickets,
            drafts: stats.total,
            generated: stats.tickets.generated,
            answered: stats.tickets.answered,
            as_is: stats.tickets.as_is,
          })}{' '}
          · {t('zendesk_ops.tz_note')}
        </Text>
      )}

      <Card>
        <SectionTitle>{t('zendesk_ops.trend.combo_title')}</SectionTitle>
        <ComboChart daily={stats.daily || []} />
        <BreakdownStrip stats={stats} />
      </Card>

      <Card>
        <SectionTitle>{t('zendesk_ops.reasons.combined_title')}</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 28 }}>
          <div>
            <Group justify="space-between" mb={6} wrap="nowrap" h={22}>
              <Text fz={11.5} fw={600} c="gray.6">
                {t('zendesk_ops.reasons.title')} · {reasonTotal}
              </Text>
            </Group>
            <Strip
              parts={[...EDIT_REASONS, 'unclassified'].map((r) => ({
                key: r,
                label: r === 'unclassified' ? t('zendesk_ops.reasons.unclassified') : t(`zendesk_ops.reason.${r}`),
                count: stats.edit_reasons?.[r] || 0,
                color: REASON_COLORS[r],
              }))}
              total={reasonTotal}
              empty="—"
            />
          </div>
          <div>
            <Group justify="space-between" mb={6} wrap="nowrap" h={22}>
              <Tooltip label={t('zendesk_ops.skip.hint')} multiline w={320} withArrow>
                <span style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Text fz={11.5} fw={600} c="gray.6">
                    {t('zendesk_ops.skip.title')} · {skipTotal}
                  </Text>
                  <IconInfoCircle size={12} color="#ADB5BD" />
                </span>
              </Tooltip>
              <Button
                size="compact-xs"
                variant="subtle"
                color="orange"
                rightSection={<IconArrowRight size={12} />}
                onClick={() => onGoQueue('knowledge_gap')}
              >
                {t('zendesk_ops.review_card.open_gap_queue')}
              </Button>
            </Group>
            <Strip
              parts={skipRows.map((r) => ({
                key: r,
                label: t(`zendesk_ops.skip.${r}`),
                count: stats.by_skip_reason?.[r] || 0,
                color: SKIP_COLORS[r],
              }))}
              total={skipTotal}
              empty="—"
            />
          </div>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)', gap: 12 }}>
        <Card>
          <SectionTitle>{t('zendesk_ops.review_card.title')}</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {(['pending', 'in_progress', 'done'] as const).map((k) => (
              <div key={k} style={{ background: '#F8F9FA', borderRadius: 10, padding: '8px 12px' }}>
                <Text fz={11} c="gray.6">
                  {t(`zendesk_ops.review.${k}`)}
                </Text>
                <Text
                  fz={20}
                  fw={700}
                  lh={1.2}
                  c={k === 'pending' && (reviewCounts?.pending || 0) > 0 ? 'red.6' : undefined}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {reviewCounts?.[k] || 0}
                </Text>
              </div>
            ))}
          </div>
          {outcomeLine && (
            <Text fz={11.5} c="gray.6" mt={10}>
              {t('zendesk_ops.review_card.outcomes')}：{outcomeLine}
            </Text>
          )}
          {gapOpen > 0 && (
            <Text fz={12} fw={600} c="orange.7" mt={6}>
              {t('zendesk_ops.review_card.gap_open', { count: gapOpen })}
            </Text>
          )}
          <Group gap={8} mt={12}>
            <Button
              size="xs"
              variant="light"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => onGoQueue('edited')}
            >
              {t('zendesk_ops.review_card.open_queue')}
            </Button>
            {gapOpen > 0 && (
              <Button size="xs" variant="subtle" color="orange" onClick={() => onGoQueue('knowledge_gap')}>
                {t('zendesk_ops.review_card.open_gap_queue')}
              </Button>
            )}
          </Group>
        </Card>
        <RecentList items={recent} onOpen={onOpen} onViewAll={() => onGoQueue('edited')} />
      </div>
    </Stack>
  );
};
